import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FiservCardInterface,
  FiservCardStatusEnum,
  List,
  Preloader,
  Text,
} from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';

import { CardAccessListItem } from './CardAccessListItem';

interface Props {
  card?: FiservCardInterface;
  lockedPursesIds: string[];
  isCardLocked?: boolean;
  isLoading?: boolean;
  onCancel: () => void;
  onSetConfirmationMode?: () => void;
  onChangeStatusMutationHandler: (status: FiservCardStatusEnum, lockedPurseIds?: string[]) => void;
}

const CardAccessFormModal: React.FC<Props> = ({
  card,
  lockedPursesIds,
  isCardLocked,
  isLoading,
  onCancel,
  onSetConfirmationMode,
  onChangeStatusMutationHandler
}) => {
  const { t } = useTranslation();
  const shouldUnlockCard = useMemo(() => !isCardLocked && lockedPursesIds.length === 0, [isCardLocked, lockedPursesIds]);

  const handleNextClick = useCallback(() => {
    if (shouldUnlockCard) {
      onChangeStatusMutationHandler(FiservCardStatusEnum.ACTIVE);
    } else {
      onSetConfirmationMode?.();
    }
  }, [onChangeStatusMutationHandler, onSetConfirmationMode, shouldUnlockCard]);

  return (
    <Box direction="column" data-testid="CardAccessFormModal">
      <Box round="moduleRound" pad="l" background="module">
        <List>
          <List.Header>
            <List.Header.Item flex={4}>
              {t('Card')}
            </List.Header.Item>
            <List.Header.Item flex={1}>
              {t('Access')}
            </List.Header.Item>
          </List.Header>    
        </List>
        <CardAccessListItem 
          card={card}
        />
        <Text color="textSecondary" margin={{ vertical: 'xs' }} >{t('You can also lock selected accounts connected to this card.')}</Text>
        <Text color="textBody" weight="bold" margin={{ top: 'xs', bottom: 'm' }}>{t(`Accounts connected to card: ${card?.cardPurses?.length || 0}`)}</Text>
        <List>
          <List.Header>
            <List.Header.Item flex={4}>
              {t('Accounts')}
            </List.Header.Item>
            <List.Header.Item flex={1}>
              {t('Access')}
            </List.Header.Item>
          </List.Header>    
        </List>
        {
          card?.cardPurses?.map((purse) => (
            <CardAccessListItem
              key={purse.id}
              purse={purse}
            />
          ))
        }
      </Box>
      <Box
        direction="row"
        fill="horizontal"
        margin={{ top: 'l' }}
        align="end"
        justify="end"
        width="medium"
        gap="xs"
      >
        <AppButton
          buttonType="secondary"
          width="140px"
          type="button"
          onClick={onCancel}
        >
          {t('Cancel')}
        </AppButton>
        <AppButton
          disabled={isLoading}
          type="button"
          onClick={handleNextClick}
          width="140px"
        >
          {isLoading ? <Preloader color="white" /> : t(shouldUnlockCard ? 'Unlock' : 'Next')}
        </AppButton>
      </Box>
    </Box>
  );
};

export default CardAccessFormModal;
